import type { CurvePoint } from './polymersBiomoleculesFood';

const WIDTH = 340;
const HEIGHT = 210;
const PAD_LEFT = 42;
const PAD_RIGHT = 14;
const PAD_TOP = 14;
const PAD_BOTTOM = 40;
const TICK_COUNT = 4;
const MARKER_RADIUS = 5;

export interface ChartSeries {
  id: string;
  points: CurvePoint[];
  /** Dashed lines give a second, non-colour cue between series. */
  dashed?: boolean;
  tone: 'coral' | 'sky';
}

const TONE_CLASS: Record<ChartSeries['tone'], string> = {
  coral: 'stroke-brand-coral',
  sky: 'stroke-brand-sky',
};

/** Line chart drawn from model points only: axes, ticks, polylines and an optional marker. */
export function ModelLineChart({
  series,
  xMax,
  yMax,
  xLabel,
  yLabel,
  ariaLabel,
  marker,
  xMin = 0,
}: {
  series: ChartSeries[];
  xMin?: number;
  xMax: number;
  yMax: number;
  xLabel: string;
  yLabel: string;
  ariaLabel: string;
  marker?: CurvePoint | null;
}) {
  const plotWidth = WIDTH - PAD_LEFT - PAD_RIGHT;
  const plotHeight = HEIGHT - PAD_TOP - PAD_BOTTOM;
  const sx = (x: number) => PAD_LEFT + ((x - xMin) / (xMax - xMin)) * plotWidth;
  const sy = (y: number) => PAD_TOP + plotHeight - (y / yMax) * plotHeight;
  const xTicks = Array.from({ length: TICK_COUNT + 1 }, (_, index) =>
    Math.round(xMin + ((xMax - xMin) * index) / TICK_COUNT),
  );
  const yTicks = Array.from({ length: TICK_COUNT + 1 }, (_, index) =>
    Math.round((yMax * index) / TICK_COUNT),
  );

  return (
    <svg
      viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
      className="mx-auto block h-auto w-full max-w-[420px]"
      role="img"
      aria-label={ariaLabel}
    >
      <line
        x1={PAD_LEFT}
        y1={PAD_TOP + plotHeight}
        x2={WIDTH - PAD_RIGHT}
        y2={PAD_TOP + plotHeight}
        className="stroke-ink"
        strokeWidth={1.5}
      />
      <line
        x1={PAD_LEFT}
        y1={PAD_TOP}
        x2={PAD_LEFT}
        y2={PAD_TOP + plotHeight}
        className="stroke-ink"
        strokeWidth={1.5}
      />
      {xTicks.map((tick) => (
        <text
          key={`x-${tick}`}
          x={sx(tick)}
          y={PAD_TOP + plotHeight + 14}
          textAnchor="middle"
          className="fill-slate2 text-[10px] font-bold"
        >
          {tick}
        </text>
      ))}
      {yTicks.map((tick) => (
        <text
          key={`y-${tick}`}
          x={PAD_LEFT - 6}
          y={sy(tick)}
          textAnchor="end"
          dominantBaseline="central"
          className="fill-slate2 text-[10px] font-bold"
        >
          {tick}
        </text>
      ))}
      <text
        x={PAD_LEFT + plotWidth / 2}
        y={HEIGHT - 6}
        textAnchor="middle"
        className="fill-ink text-[11px] font-extrabold"
      >
        {xLabel}
      </text>
      <text
        x={12}
        y={PAD_TOP + plotHeight / 2}
        textAnchor="middle"
        transform={`rotate(-90 12 ${PAD_TOP + plotHeight / 2})`}
        className="fill-ink text-[11px] font-extrabold"
      >
        {yLabel}
      </text>
      {series.map((line) => (
        <polyline
          key={line.id}
          points={line.points.map((point) => `${sx(point.x)},${sy(point.y)}`).join(' ')}
          className={`fill-none ${TONE_CLASS[line.tone]}`}
          strokeWidth={3}
          strokeLinejoin="round"
          strokeDasharray={line.dashed ? '7 5' : undefined}
        />
      ))}
      {marker ? (
        <circle
          cx={sx(marker.x)}
          cy={sy(marker.y)}
          r={MARKER_RADIUS}
          className="fill-ink stroke-white"
          strokeWidth={2}
        />
      ) : null}
    </svg>
  );
}
